import { AfterViewInit, ChangeDetectorRef, Component, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import { Button } from 'primeng/button';
import { Tabs } from 'primeng/tabs';
import { TabList } from 'primeng/tabs';
import { Tab } from 'primeng/tabs';
import { TabPanels } from 'primeng/tabs';
import { TabPanel } from 'primeng/tabs';
import { CompaniesTableComponent } from '../../components/companies-table/companies-table.component';
import { Tooltip } from 'primeng/tooltip';
import { filter, finalize, switchMap, take } from 'rxjs';
import { DataTableComponent } from '../../../shared/components/data-table/data-table.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { TableColumn, DataTableColumnButton, TableLazyEvent } from '../../../shared/contracts/table-column.interface';
import { NotificationService } from '../../../shared/services/notification.service';
import { DialogAction } from '../../../shared/contracts/dialog.interface';
import { ConfirmDialogComponent } from '../../../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { Contact, ContactTeam } from '../../contracts/contact.interface';
import { Company } from '../../contracts/company.interface';
import { ContactService } from '../../services/contact.service';
import { CompanyService } from '../../services/company.service';
import { ContactDialogComponent } from '../dialogs/contact-dialog/contact-dialog.component';
import { CompanyDialogComponent } from '../dialogs/company-dialog/company-dialog.component';
import { CONVERSATION_AVATAR_COLORS } from '../../../shared/constants/conversation-avatar-map';

@Component({
  selector: 'kc-contacts-page',
  standalone: true,
  imports: [
    CommonModule,
    Toast,
    Button,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    CompaniesTableComponent,
    DataTableComponent,
    LoaderComponent,
    Tooltip,
  ],
  providers: [ContactService, CompanyService, DialogService, NotificationService],
  templateUrl: './contacts-page.component.html',
  styleUrl: './contacts-page.component.scss',
})
export class ContactsPageComponent implements AfterViewInit {
  @ViewChild('nameTpl') nameTpl!: TemplateRef<any>;
  @ViewChild('companyTpl') companyTpl!: TemplateRef<any>;
  @ViewChild('phoneTpl') phoneTpl!: TemplateRef<any>;
  @ViewChild('operatorsTpl') operatorsTpl!: TemplateRef<any>;
  @ViewChild('teamsTpl') teamsTpl!: TemplateRef<any>;

  activeTab: string | number = 'contacts';

  contacts: Contact[] = [];
  totalContacts = 0;
  contactsLoading = false;
  rows = 20;
  lastLazyEvent: TableLazyEvent | undefined;

  contactColumns: TableColumn[] = [
    { header: 'Nome e Cognome', type: 'template', field: 'firstName', filterable: true, filterFields: ['first_name', 'last_name'] },
    { header: 'Azienda', type: 'template', field: 'companyName', filterable: true, filterFields: ['company_name'] },
    {
      field: 'email',
      header: 'Email',
      transformer: (row: Contact) => row.email ?? '—',
    },
    { header: 'Telefono', type: 'template', field: 'phoneNumber', filterable: true, filterFields: ['phone_number'] },
    { header: 'Operatori', type: 'template' },
    { header: 'Teams', type: 'template' },
    {
      header: 'Azioni',
      buttons: [
        {
          icon: 'pi-pencil',
          tooltip: 'Modifica',
          color: 'primary',
          click: (row: Contact) => this.editContact(row),
        },
        {
          icon: 'pi-trash',
          tooltip: 'Elimina',
          color: 'danger',
          click: (row: Contact) => this.deleteContact(row),
        },
      ] as DataTableColumnButton[],
    },
  ];

  companies: Company[] = [];
  companiesLoading = false;

  _refDialog: DynamicDialogRef | undefined;

  constructor(
    private contactService: ContactService,
    private companyService: CompanyService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit() {
    this.contactColumns[0].customTemplate = this.nameTpl;
    this.contactColumns[1].customTemplate = this.companyTpl;
    this.contactColumns[3].customTemplate = this.phoneTpl;
    this.contactColumns[4].customTemplate = this.operatorsTpl;
    this.contactColumns[5].customTemplate = this.teamsTpl;
    this.cdr.detectChanges();
  }

  getInitials(contact: Contact): string {
    const f = contact.firstName?.charAt(0) ?? '';
    const l = contact.lastName?.charAt(0) ?? '';
    return (f + l).toUpperCase() || '?';
  }

  getAvatarStyle(contact: Contact): { background: string; color: string } {
    const idx = ((contact.id ?? 0) % 10) + 1;
    return CONVERSATION_AVATAR_COLORS[idx];
  }


  getUserInitials(user: { firstName?: string; lastName?: string; email?: string }): string {
    const f = user.firstName?.charAt(0) ?? '';
    const l = user.lastName?.charAt(0) ?? '';
    if (f || l) return (f + l).toUpperCase();
    return user.email?.charAt(0).toUpperCase() ?? '?';
  }

  getUserAvatarStyle(user: { id: number }): { background: string; color: string } {
    return CONVERSATION_AVATAR_COLORS[(user.id % 10) + 1];
  }

  getUserDisplayName(user: { firstName?: string; lastName?: string; email?: string }): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || '';
  }

  getTeamMembersTooltip(team: ContactTeam): string {
    if (!team.members?.length) return team.name;
    return team.members.map(m => this.getUserDisplayName(m)).join(', ');
  }

  getExtraTeamsTooltip(contact: Contact): string {
    return (contact.teams ?? []).slice(1).map(t => t.name).join(', ');
  }

  primaryPhone(contact: Contact): string {
    const phones = contact.phoneNumbers ?? [];
    const primary = [...phones].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))[0];
    return primary?.phoneNumber ?? '';
  }

  onTabChange(value: string | number) {
    if (value === 'companies') {
      this.loadCompanies();
    } else if (value === 'contacts' && this.lastLazyEvent) {
      this.loadContacts(this.lastLazyEvent);
    }
  }

  // --- Contacts ---

  loadContacts(event: TableLazyEvent): void {
    this.contactsLoading = true;
    this.lastLazyEvent = event;
    this.contactService
      .getAll(event.page, event.rows, event.filters)
      .pipe(
        take(1),
        finalize(() => (this.contactsLoading = false)),
      )
      .subscribe(data => {
        this.contacts = data.data;
        this.totalContacts = data.total;
      });
  }

  createContact(): void {
    this._refDialog = this.dialogService.open(ContactDialogComponent, {
      ...ContactDialogComponent.DIALOG_CONFIG,
    });
    this._refDialog.onClose
      .pipe(filter(result => result?.action === DialogAction.OK))
      .subscribe(() => {
        this.loadContacts(this.lastLazyEvent!);
        this.notificationService.success('Contatto creato con successo.');
      });
  }

  editContact(contact: Contact): void {
    this._refDialog = this.dialogService.open(ContactDialogComponent, {
      ...ContactDialogComponent.DIALOG_CONFIG,
      header: 'Modifica Contatto',
      data: contact,
    });
    this._refDialog.onClose
      .pipe(filter(result => result?.action === DialogAction.OK))
      .subscribe(() => {
        this.loadContacts(this.lastLazyEvent!);
        this.notificationService.success('Contatto aggiornato con successo.');
      });
  }

  deleteContact(contact: Contact): void {
    this._refDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: {
        message: `Sei sicuro di voler eliminare ${contact.firstName} ${contact.lastName ?? ''}?`,
      },
    });
    this._refDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(() => this.contactService.delete(contact.id!)),
      )
      .subscribe(() => {
        this.loadContacts(this.lastLazyEvent!);
        this.notificationService.success('Contatto eliminato con successo.');
      });
  }

  // --- Companies ---

  loadCompanies(): void {
    this.companiesLoading = true;
    this.companyService
      .getAll()
      .pipe(
        take(1),
        finalize(() => (this.companiesLoading = false)),
      )
      .subscribe(data => (this.companies = data.data));
  }

  createCompany(): void {
    this._refDialog = this.dialogService.open(CompanyDialogComponent, {
      ...CompanyDialogComponent.DIALOG_CONFIG,
    });
    this._refDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(result => this.companyService.create(result.data)),
      )
      .subscribe(() => {
        this.loadCompanies();
        this.notificationService.success('Azienda creata con successo.');
      });
  }

  editCompany(company: Company, event?: Event): void {
    event?.stopPropagation();
    this._refDialog = this.dialogService.open(CompanyDialogComponent, {
      ...CompanyDialogComponent.DIALOG_CONFIG,
      header: 'Modifica Azienda',
      data: company,
    });
    this._refDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(result => this.companyService.update(company.id!, result.data)),
      )
      .subscribe(() => {
        this.loadCompanies();
        this.notificationService.success('Azienda aggiornata con successo.');
      });
  }

  deleteCompany(company: Company, event?: Event): void {
    event?.stopPropagation();
    this._refDialog = this.dialogService.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponent.DIALOG_CONFIG,
      data: { message: `Sei sicuro di voler eliminare l'azienda ${company.name}?` },
    });
    this._refDialog.onClose
      .pipe(
        filter(result => result?.action === DialogAction.OK),
        switchMap(() => this.companyService.delete(company.id!)),
      )
      .subscribe(() => {
        this.loadCompanies();
        this.notificationService.success('Azienda eliminata con successo.');
      });
  }

  formatPhones(contact: Contact): string {
    const phones = contact.phoneNumbers ?? [];
    if (!phones.length) return '—';
    const sorted = [...phones].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
    return sorted.map(p => p.phoneNumber).join(', ');
  }

  formatOperators(contact: Contact): string {
    const users = contact.operators ?? [];
    if (!users.length) return '—';
    return users.map(u => u.email ?? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()).join(', ');
  }
}
